/**
 * ProviderSubHubView — Tier-2 of the Cloud GM AI Control Hub.
 *
 * Shows the provider brand banner and 4 navigation tiles:
 *   Control Panel · Bot Studio · Training Library · Usage Log
 */
import { motion } from 'framer-motion';
import {
  ChevronRight, History, Bot, Database, Sparkles,
} from 'lucide-react';
import { PROVIDER_META } from './constants';
import { ProviderLogo } from './ProviderLogo';

export function ProviderSubHubView({ language, provider, onPick }) {
  const meta = PROVIDER_META[provider.provider] || PROVIDER_META.Google;
  // iter-232b — "AI Management" tile removed per user request. Pause/resume
  // and per-bot editing now live entirely under the AI Models Control page.
  const tiles = [
    {
      key: 'training',
      testId: 'ai-pick-training',
      icon: Database,
      hoverBorder: 'hover:border-purple-500/50',
      iconBg: 'bg-purple-500/15 border-purple-500/30 text-purple-300',
      arrow: 'group-hover:text-purple-300',
      titleAr: 'مكتبة التدريب', titleEn: 'Training Library',
      subAr: 'رفع Excel وحقنه في البوتات', subEn: 'Upload Excel & feed to bots',
      badge: { icon: Sparkles, ar: 'جديد', en: 'New', class: 'bg-purple-500/15 border-purple-500/40 text-purple-300' },
    },
    {
      key: 'usage',
      testId: 'ai-pick-usage',
      icon: History,
      hoverBorder: 'hover:border-blue-500/40',
      iconBg: 'bg-blue-500/15 border-blue-500/30 text-blue-300',
      arrow: 'group-hover:text-blue-300',
      titleAr: 'سجل الاستخدام', titleEn: 'Usage Log',
      subAr: 'كل نداء: التوكنز، التكلفة، الحالة', subEn: 'Every call: tokens, cost, status',
    },
  ];

  return (
    <div className="space-y-5" data-testid="ai-provider-subhub">
      <div
        className={`bg-gradient-to-br ${meta.gradient} border rounded-2xl p-5 flex items-center gap-4`}
        style={{ borderColor: `${meta.bg}50` }}
      >
        <ProviderLogo provider={provider.provider} size={56} />
        <div>
          <h3 className="text-xl font-bold text-white">
            {language === 'ar' ? provider.label_ar : provider.label_en}
          </h3>
          <p className="text-xs text-white/60 mt-0.5">
            {language === 'ar' ? provider.tagline_ar : provider.tagline_en}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <SubHubTile key={t.key} tile={t} language={language} onPick={onPick} />
        ))}
      </div>
    </div>
  );
}

function SubHubTile({ tile, language, onPick }) {
  const Icon = tile.icon;
  const Badge = tile.badge?.icon;
  return (
    <motion.button
      type="button"
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onPick(tile.key)}
      className={`relative text-start bg-white/[0.02] border border-white/10 ${tile.hoverBorder} rounded-2xl p-5 transition-all overflow-hidden group`}
      data-testid={tile.testId}
    >
      {tile.badge && (
        <div className="absolute top-3 end-3">
          <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${tile.badge.class}`}>
            <Badge className="h-2.5 w-2.5" />
            {language === 'ar' ? tile.badge.ar : tile.badge.en}
          </span>
        </div>
      )}
      <div className={`w-11 h-11 rounded-xl border flex items-center justify-center mb-3 ${tile.iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="text-base font-bold text-white">
        {language === 'ar' ? tile.titleAr : tile.titleEn}
      </h4>
      <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">
        {language === 'ar' ? tile.subAr : tile.subEn}
      </p>
      <ChevronRight
        className={`absolute bottom-4 ${language === 'ar' ? 'left-4 rotate-180' : 'right-4'} h-4 w-4 text-white/30 ${tile.arrow}`}
      />
    </motion.button>
  );
}
